import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Spin,
  Typography,
  Divider,
  Modal,
} from "antd";
import { EditOutlined, DeleteOutlined, SaveOutlined, LoadingOutlined } from "@ant-design/icons";
import Layout from "../Layout";

const { Title, Text } = Typography;

const WooCommerceCredentialsPage = () => {
  const [form] = Form.useForm();
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        setFetching(true);
        const response = await fetch("https://api.21stcenturyav.info/woocommerce/credentials", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 404) {
          // Credentials not set
          setCredentials(null);
          setFetching(false);
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch credentials.");
        }

        const data = await response.json();
        setCredentials(data);
      } catch (error) {
        console.error("Error fetching credentials:", error);
        message.error(error.message || "Error fetching credentials.");
      } finally {
        setFetching(false);
      }
    };

    fetchCredentials();
  }, [accessToken]);

  const handleSetCredentials = async (values) => {
    try {
      setLoading(true);
      const response = await fetch("https://api.21stcenturyav.info/woocommerce/credentials", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to set credentials.");
      }

      const data = await response.json();
      message.success(data.message || "Credentials set successfully.");
      setCredentials(values);
      form.resetFields();
    } catch (error) {
      console.error("Error setting credentials:", error);
      message.error(error.message || "Error setting credentials.");
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = () => {
    Modal.confirm({
      title: "Are you sure you want to delete WooCommerce credentials?",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: handleDeleteCredentials,
    });
  };

  const handleDeleteCredentials = async () => {
    try {
      setDeleting(true);
      const response = await fetch("https://api.21stcenturyav.info/woocommerce/credentials", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete credentials.");
      }

      const data = await response.json();
      message.success(data.message || "Credentials deleted successfully.");
      setCredentials(null);
    } catch (error) {
      console.error("Error deleting credentials:", error);
      message.error(error.message || "Error deleting credentials.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout pageTitle="WooCommerce Credentials">
      <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
        <Spin spinning={fetching} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
          <Card
            title={
              <Title level={3} style={{ margin: 0 }}>
                WooCommerce API Credentials
              </Title>
            }
            bordered={false}
            extra={
              credentials ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setIsModalVisible(true)}
                >
                  Edit Credentials
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => setIsModalVisible(true)}
                >
                  Set Credentials
                </Button>
              )
            }
          >
            {credentials ? (
              <>
                <Alert
                  message="WooCommerce credentials are currently set."
                  type="success"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
                <div>
                  <Text strong>Store URL:</Text>
                  <p>{credentials.url}</p>
                </div>
                <Divider />
                <div>
                  <Text strong>Consumer Key:</Text>
                  <p>{credentials.consumer_key}</p>
                </div>
                <Divider />
                <div>
                  <Text strong>Consumer Secret:</Text>
                  <p>{credentials.consumer_secret}</p>
                </div>
                <Divider />
                <Button
                  type="danger"
                  icon={<DeleteOutlined />}
                  onClick={showDeleteConfirm}
                  loading={deleting}
                >
                  Delete Credentials
                </Button>
              </>
            ) : (
              <Alert
                message="WooCommerce credentials are not set."
                description="Please set your WooCommerce API credentials to enable product synchronization."
                type="warning"
                showIcon
              />
            )}
          </Card>
        </Spin>

        {/* Modal for Setting/Editing Credentials */}
        <Modal
          title={credentials ? "Edit WooCommerce Credentials" : "Set WooCommerce Credentials"}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              url: credentials?.url || "",
              consumer_key: credentials?.consumer_key || "",
              consumer_secret: credentials?.consumer_secret || "",
            }}
            onFinish={handleSetCredentials}
          >
            <Form.Item
              label={<Text strong>Store URL</Text>}
              name="url"
              rules={[
                { required: true, message: "Please enter your WooCommerce store URL." },
                { type: "url", message: "Please enter a valid URL." },
              ]}
            >
              <Input
                placeholder="https://yourstore.com/"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label={<Text strong>Consumer Key</Text>}
              name="consumer_key"
              rules={[
                { required: true, message: "Please enter your Consumer Key." },
              ]}
            >
              <Input
                placeholder="ck_your_consumer_key"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label={<Text strong>Consumer Secret</Text>}
              name="consumer_secret"
              rules={[
                { required: true, message: "Please enter your Consumer Secret." },
              ]}
            >
              <Input.Password
                placeholder="cs_your_consumer_secret"
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                block
              >
                {credentials ? "Update Credentials" : "Set Credentials"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default WooCommerceCredentialsPage;
